import { useUserPermissions } from './use-permission';
import { NavigateOptions, Path as NavigatePath, useNavigate, useLocation } from 'react-router-dom';
import { urlReplacer } from '../utils/url-helpers';
import { ROUTES } from '../constants/routes';
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { isEmbedAtom } from '../../modules/embed/state';
import { mergeNavigateOptionsForSalesforceEmbed } from '../../modules/embed/utils/embed-client-context';

export const useCustomNavigate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const customNavigate = useMemo(() => {
    return (
      navigateTo: number | (Partial<NavigatePath> & { href?: string }),
      navigateOptions?: NavigateOptions & { clearExistingParams?: boolean }
    ): void => {
      if (typeof navigateTo === 'number') {
        navigate(navigateTo);
        return;
      }
      const mergedOptions = mergeNavigateOptionsForSalesforceEmbed(navigateOptions);
      const { pathname, search, hash, href } = navigateTo;
      if (typeof href === 'string' && href.length > 0) {
        navigate(href, mergedOptions);
        return;
      }
      let existingUrlParams = new URLSearchParams(location.search);
      const newUrlParams = new URLSearchParams(search);
      const mergedUrlParams = new URLSearchParams(
        navigateOptions?.clearExistingParams
          ? Object.fromEntries(newUrlParams)
          : {
              ...Object.fromEntries(existingUrlParams),
              ...Object.fromEntries(newUrlParams),
            }
      ).toString();
      navigate(
        {
          pathname,
          search: mergedUrlParams.toString(),
          hash: typeof hash === 'string' && hash.length > 0 ? hash : location.hash,
        },
        mergedOptions
      );
    };
  }, [navigate, location.pathname, location.search, location.hash]);
  return customNavigate;
};

const useConversationNavigate = () => {
  const {
    canReadCustomerPhone: { hasAccess: phoneAccess },
  } = useUserPermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const isEmbed = useAtomValue(isEmbedAtom);
  const navigateToConversation = ({
    phoneNumber,
    chatTypeId,
    navigateOptions,
    waba,
    openInNewTab = false,
    messageId,
  }: {
    phoneNumber: string;
    chatTypeId?: string;
    navigateOptions?: NavigateOptions;
    waba: string;
    openInNewTab?: boolean;
    messageId?: string;
  }): void => {
    const pathname = urlReplacer(
      `${isEmbed ? ROUTES.EMBED.replace('/*', '') : ''}${ROUTES.CONVERSATIONS}/:integrationWabaNumber/:id/:messageId`,
      [
        [':id', chatTypeId ?? phoneNumber],
        [':integrationWabaNumber', waba],
        [':messageId', messageId ?? ''],
      ]
    );

    const finalUrl = `${pathname}${location.search}`;

    if (openInNewTab) {
      window.open(finalUrl, '_blank');
      return;
    }

    navigate(
      {
        pathname,
        search: location.search,
      },
      mergeNavigateOptionsForSalesforceEmbed(navigateOptions)
    );
  };
  return {
    navigateToConversation,
  };
};

export default useConversationNavigate;
